import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterLogicalOperator,
  IABCategories,
  LineItem,
  Package,
  QueryParams,
} from '@/common/models';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import { convertRangesToDaypart, daypartToRanges, parseRanges } from '@/common/models/Dayparting';
import EntityProperty from '@/common/models/EntityProperty';
import PackageOptions from '@/common/models/package/PackageOptions';
import PurchaseTypeOptions from '@/common/models/PurchaseTypeOptions';
import { FullDaypart, InvalidDaypart } from '@/globals';
import { useExternalExchanges, useMediaOwners, useMediaTypes, useNetworks } from '@/org-app/services/tanstack/queries';
import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
import queryClient, { runQueryInScope, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import {
  UPDATE_PACKAGE_CREATIVEFORMATEXCLUSIONS,
  UPDATE_PACKAGE_CREATIVEFORMATS,
  UPDATE_PACKAGE_CREATIVEORIENTATIONEXCLUSIONS,
  UPDATE_PACKAGE_CREATIVEORIENTATIONS,
  UPDATE_PACKAGE_DAYPART,
  UPDATE_PACKAGE_DEMOGRAPHIC_TARGET_IDS,
  UPDATE_PACKAGE_ENDDATE,
  UPDATE_PACKAGE_GEOFENCE_GROUP_EXCLUSION_IDS,
  UPDATE_PACKAGE_GEOFENCE_GROUP_IDS,
  UPDATE_PACKAGE_IABCATEGORIES,
  UPDATE_PACKAGE_IABCATEGORYEXCLUSIONS,
  UPDATE_PACKAGE_ID,
  UPDATE_PACKAGE_LANGUAGE_EXCLUSION_IDS,
  UPDATE_PACKAGE_LANGUAGE_IDS,
  UPDATE_PACKAGE_LOCATION_TARGET_IDS,
  UPDATE_PACKAGE_MARKET_IDS,
  UPDATE_PACKAGE_MEDIA_OWNER_IDS,
  UPDATE_PACKAGE_MEDIA_TYPE_IDS,
  UPDATE_PACKAGE_NAME,
  UPDATE_PACKAGE_NETWORK_IDS,
  UPDATE_PACKAGE_NOTES,
  UPDATE_PACKAGE_PURCHASE_TYPE,
  UPDATE_PACKAGE_SITE_EXCLUSION_IDS,
  UPDATE_PACKAGE_SITE_IDS,
  UPDATE_PACKAGE_STARTDATE,
  UPDATE_PACKAGE_UNIT_IDS,
  UPDATE_PACKAGE_UNITPACK_EXCLUSION_IDS,
  UPDATE_PACKAGE_UNITPACK_IDS,
  UPDATE_PACKAGE_UNITTAGEXCLUSIONS,
  UPDATE_PACKAGE_UNITTAGS,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

export const getEntityProperties = () => {
  const action = (pack) => {
    if (isCuratorOrganization()) {
      return rootStore.dispatch('orgApp/updateCuratedPackage', { instance: pack, showLoader: false });
    }
    return rootStore.dispatch('orgApp/savePackage', { pack, showLoader: false });
  };
  const isCuratorOrganization = () => orgAppStore.getters.isCuratorOrganization(orgAppStore.state);

  let isDemandOrganization = false;
  let isSupplyOrganization = false;

  if (rootState.orgApp?.organization != null) {
    isDemandOrganization = rootStore.getters.hasChosenExchange
      ? false
      : rootState.orgApp.organization.isDemandOrganization;
    isSupplyOrganization = rootStore.getters.hasChosenExchange
      ? false
      : rootState.orgApp.organization.isSupplyOrganization;
  }

  const isEditableHandler = () => !isDemandOrganization;
  const isEndDateEditable = (pack) => !pack.entity?.isEvergreen;
  const getExtraValidation = (pack) => pack.getExtraValidation();

  const getMediaOwners = () => runQueryInScope(useMediaOwners);

  const getExternalExchanges = () => runQueryInScope(useExternalExchanges);

  const getLocationEntityProperty = (labelKey: string, level: number, isExclusion = false) => {
    return new EntityProperty({
      label: capitalize(i18n.global.t(labelKey, { n: 2 })),
      property: isExclusion ? 'location_exclusion_ids' : 'location_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: isExclusion ? 'locationExclusionIDs' : 'locationIDs',
      mutation: UPDATE_PACKAGE_LOCATION_TARGET_IDS,
      editableClassName: Package.type,
      source: ({ queryParams = QueryParams.all(FilterCollection.for('level', [level])) }) => {
        return rootStore.dispatch('orgApp/getLocations', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [
              new Filter('level', [level]),
              new Filter(
                'location_id',
                isExclusion ? entity.locationExclusionIDs : entity.locationIDs,
                FilterComparisonOperator.IN,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ),
      ignoreUnmatchedValuesInSource: true,
    });
  };

  //
  // Logic to fetch markets, only one call will be performed.
  //
  const getMarkets = async () => {
    if (Package.isGettingMarkets) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Package.isGettingMarkets && Package.marketsCache != null) {
            clearInterval(interval);
            resolve(Package.marketsCache);
          }
        }, 500);
      });
    }
    Package.isGettingMarkets = true;
    const mediaOwnerCountryRoots = await rootStore.dispatch('orgApp/getSupplierCountryRoots', { showLoader: false });
    const allMediaOwnerCountryIDs: number[][] = Object.values(mediaOwnerCountryRoots);
    const countryIDs: number[] = Array.from(new Set(allMediaOwnerCountryIDs.reduce((mem, x) => [...mem, ...x], [])));
    let ownerIDs: number[] = [];
    const { isSupplyOrganization, isCuratorOrganization } = rootState.orgApp.organization;
    if (isSupplyOrganization) {
      ownerIDs = [rootState.orgApp.organization.id];
    } else {
      const mediaOwners = await getMediaOwners();
      ownerIDs = mediaOwners.map((mediaOwner) => mediaOwner.id);
    }
    const orgExchAppDispatcher = isCuratorOrganization ? 'exchApp' : 'orgApp';
    Package.marketsCache = await rootStore.dispatch(`${orgExchAppDispatcher}/getMarketsWithInventory`, {
      ownerIDs,
      countryIDs,
      showLoader: false,
    });
    Package.isGettingMarkets = false;
    return Package.marketsCache;
  };

  //
  // Logic to fetch networks, only one call will be performed.
  //
  const getNetworks = async () => {
    const qry = useNetworks(queryClient);
    await waitForPendingQueries([qry]);
    return qry.items.value;
  };

  //
  // Logic to fetch mediaTypes, only one call will be performed.
  //
  const getMediaTypes = () => runQueryInScope(useMediaTypes);

  //
  // Logic to fetch unitPacks, only one call will be performed.
  //
  const getUnitPacks = async () => {
    if (Package.isGettingUnitPacks) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Package.isGettingUnitPacks && Package.unitPacksCache != null) {
            clearInterval(interval);
            resolve(Package.unitPacksCache);
          }
        }, 100);
      });
    }
    const queryParams = QueryParams.all(FilterCollection.for('active', [false]));
    Package.isGettingUnitPacks = true;
    Package.unitPacksCache = await rootStore.dispatch('orgApp/getUnitPacks', { queryParams, showLoader: false });
    Package.isGettingUnitPacks = false;
    return Package.unitPacksCache;
  };

  return {
    id: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.id', { n: 1 })),
      property: 'package_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'id',
      mutation: UPDATE_PACKAGE_ID,
      editableClassName: Package.type,
      action,
      isEditableHandler,
      getExtraValidation,
    }),
    name: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.name', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_PACKAGE_NAME,
      editableClassName: Package.type,
      action,
      isEditableHandler,
      getExtraValidation,
    }),
    dealIDFormatted: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.dealID', { n: 1 })),
      type: TableColumnTypes.string,
      property: 'deal_id',
      editablePropertyName: 'dealID',
    }),
    purchaseType: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.purchaseType', { n: 1 })),
      property: 'purchase_type',
      type: TableColumnTypes.string,
      editablePropertyName: 'purchaseType',
      editableClassName: Package.type,
      mutation: UPDATE_PACKAGE_PURCHASE_TYPE,
      source: PurchaseTypeOptions.options().map((x) => ({
        id: x.value,
        name: x.name,
        filters: x.filters,
      })),
    }),
    status: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.status', { n: 1 })),
      type: TableColumnTypes.list,
      property: 'state',
      source:
        isSupplyOrganization || isCuratorOrganization()
          ? PackageOptions.supplyStatusOptions().map((x) => ({ id: x.value, name: x.name, filters: x.filters }))
          : undefined,
    }),
    createdBy: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.createdBy')),
      property: 'created_by',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useUserOrganizations),
      sourceFilterProperty: 'created_by',
      sourceLabel: 'email',
      sourceID: 'userID',
    }),
    dsp: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.dsp', { n: 1 })),
      property: 'buyer_organization_dsp_ids',
      type: TableColumnTypes.list,
    }),
    dspBuyers: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.buyer', { n: 1 })),
      property: 'buyer_organization_dsp_ids',
      type: TableColumnTypes.list,
      source: () => rootStore.dispatch('orgApp/getOrganizationDSPs'),
      sourceFilterProperty: 'package_organization_restrictions.organization_dsp_id',
    }),
    publisherDealID: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.publisherDealID')),
      property: 'package_children',
      type: TableColumnTypes.string,
    }),
    dspExchanges: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.dsp', { n: 1 })),
      property: 'buyer_external_exchange_ids',
      type: TableColumnTypes.list,
      source: getExternalExchanges,
      sourceFilterProperty: 'package_external_exchange_restrictions.external_exchange_id',
      sourceID: 'externalExchangeID',
    }),
    offeredTo: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.offeredTo')),
      property: 'package_organization_restrictions.organization_dsp_id',
      type: TableColumnTypes.string,
    }),
    mediaOwner: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
      property: 'mediaOwnerIDs',
      type: TableColumnTypes.list,
      source: getMediaOwners,
      sourceFilterProperty: 'package_mediaowners.mediaowner_id',
    }),
    startDate: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.startDate', { n: 1 })),
      property: 'flight_from',
      type: TableColumnTypes.date,
      editablePropertyName: 'startDate',
      mutation: (payload) => {
        rootStore.commit(UPDATE_PACKAGE_STARTDATE, payload);
      },
      editableClassName: Package.type,
      action,
      isEditableHandler,
      getExtraValidation,
    }),
    endDate: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.endDate', { n: 1 })),
      property: 'flight_to',
      type: TableColumnTypes.date,
      editablePropertyName: 'endDate',
      isEditableHandler: isEndDateEditable,
      mutation: (payload) => {
        rootStore.commit(UPDATE_PACKAGE_ENDDATE, payload);
      },
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    impressionsDescription: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.impression', { n: 2 })),
      property: 'impressions',
      type: TableColumnTypes.integer,
    }),
    budgetDescription: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.budget', { n: 1 })),
      property: 'budget',
      type: TableColumnTypes.currency,
    }),
    cpmDescription: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cpmDescription',
      type: TableColumnTypes.string,
    }),
    // Filters only
    impressionsMin: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.impressionsMin', { n: 2 })),
      property: 'market_targeted_impressions_min',
      type: TableColumnTypes.integer,
    }),
    impressionsMax: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.impressionsMax', { n: 2 })),
      property: 'market_targeted_impressions_max',
      type: TableColumnTypes.integer,
    }),
    budgetMin: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.budgetMin', { n: 1 })),
      property: 'budget_min',
      type: TableColumnTypes.currency,
    }),
    budgetMax: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.budgetMax', { n: 1 })),
      property: 'budget_max',
      type: TableColumnTypes.currency,
    }),
    cpmFixed: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cpm_fixed',
      isMoney: true,
      editablePropertyName: 'cpmFixed',
      type: TableColumnTypes.currency,
    }),
    availableDeals: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.currentlyAvailable', { n: 1 })),
      property: 'available',
      type: TableColumnTypes.boolean,
    }),
    modifiedOnFormatted: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.lastModifiedOn')),
      property: 'modified_on_utc',
      type: TableColumnTypes.datetime,
    }),
    notes: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.notes', { n: 1 })),
      property: 'notes',
      type: TableColumnTypes.string,
      mutation: UPDATE_PACKAGE_NOTES,
      editableClassName: Package.type,
      action,
      isEditableHandler,
    }),
    planID: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.planID', { n: 1 })),
      property: 'plan_id',
      type: TableColumnTypes.integer,
    }),
    countries: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.country', { n: 1 })),
      property: 'countries',
      type: TableColumnTypes.list,
    }),
    dealVisibility: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.dealVisibility')),
      type: TableColumnTypes.list,
      source: PackageOptions.packageRestrictionOptions().map((x) => ({
        id: x.value,
        name: x.name,
      })),
    }),
    directBilling: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.directBilling')),
      type: TableColumnTypes.boolean,
      property: 'direct_billing',
      editablePropertyName: 'directBilling',
    }),
    isEvergreen: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.alwaysOn')),
      type: TableColumnTypes.boolean,
      property: 'is_evergreen',
      editablePropertyName: 'isEvergreen',
    }),
    hideUnitInformationInBidRequests: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('messages.hideScreenInformationInBidRequests')),
      type: TableColumnTypes.boolean,
      property: 'hide_unit_information_in_bid_request',
      editablePropertyName: 'hideUnitInformationInBidRequests',
    }),
    formattedDSPName: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.buyer', { n: 2 })),
      type: TableColumnTypes.string,
      property: 'formattedDSPName',
    }),
    auctionType: new EntityProperty<Package>({
      label: capitalize(i18n.global.t('names.buyer', { n: 2 })),
      type: TableColumnTypes.string,
      property: 'auction_type',
      editablePropertyName: 'auctionType',
      toStringOverride: (value) => value.auctionTypeFormatted,
    }),
    isCPMFromRateCard: new EntityProperty({
      label: capitalize(i18n.global.t('messages.isCPMFromRateCard')),
      property: 'is_cpm_from_rate_card',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isCPMFromRateCard',
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    adDurationType: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDurationType', { n: 1 })),
      property: 'adDurationTypeDescription',
      type: TableColumnTypes.list,
      action,
      getExtraValidation,
    }),
    adDuration: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDuration', { n: 1 })),
      property: 'ad_duration_min',
      type: TableColumnTypes.integer,
      editablePropertyName: 'adDurationMin',
      action,
      getExtraValidation,
      toStringOverride: (value) => value.adDurationDescription,
    }),

    markets: new EntityProperty({
      label: capitalize(i18n.global.t('names.market', { n: 2 })),
      property: 'market_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketIDs',
      mutation: UPDATE_PACKAGE_MARKET_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    networks: new EntityProperty({
      label: capitalize(i18n.global.t('names.network', { n: 2 })),
      property: 'network_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkIDs',
      mutation: UPDATE_PACKAGE_NETWORK_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    unitPacks: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPack', { n: 2 })),
      property: 'unitpack_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackIDs',
      mutation: UPDATE_PACKAGE_UNITPACK_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    unitPackExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPackExclusion', { n: 2 })),
      property: 'unitpack_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackExclusionIDs',
      mutation: UPDATE_PACKAGE_UNITPACK_EXCLUSION_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    mediaOwners: new EntityProperty({
      label: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
      property: 'mediaowner_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaOwners,
      sourceFilterProperty: 'owner_id',
      editablePropertyName: 'mediaOwnerIDs',
      mutation: UPDATE_PACKAGE_MEDIA_OWNER_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
      defaultValue: async () => (await getMediaOwners()).map((x) => ({ label: x.name, value: x.id })),
      toStringOverride: (value) => [...new Set(value.mediaOwnerIDs.map((x) => x.label))].join(', '),
    }),
    mediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediatype_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeIDs',
      mutation: UPDATE_PACKAGE_MEDIA_TYPE_IDS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    daypart: new EntityProperty({
      label: capitalize(i18n.global.t('names.daypart', { n: 2 })),
      property: 'daypart',
      type: TableColumnTypes.string,
      editablePropertyName: 'daypartRanges',
      validationPropertyName: 'daypart',
      mutation: UPDATE_PACKAGE_DAYPART,
      editableClassName: Package.type,
      preMutationTransform: (text) => {
        if (text == null || text === '') return FullDaypart;
        try {
          const result = parseRanges(text);
          return convertRangesToDaypart(result.ranges);
        } catch (e) {
          return InvalidDaypart;
        }
      },
      action,
      getExtraValidation,
      canAffectAdDeliveryWhenModified: true,
      bulkInputNote: 'Note that hours are inclusive.',
      bulkInputExamples: ['Mon-Sun 6-17', 'Mon 12-16 Tue-Fri 8-9'],
      defaultValue: {
        label: daypartToRanges(FullDaypart),
        value: FullDaypart,
      },
    }),
    creativeOrientations: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
      property: 'creative_orientations',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientations',
      mutation: UPDATE_PACKAGE_CREATIVEORIENTATIONS,
      editableClassName: Package.type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeOrientationExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientationExclusion', { n: 2 })),
      property: 'creative_orientation_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientationExclusions',
      mutation: UPDATE_PACKAGE_CREATIVEORIENTATIONEXCLUSIONS,
      editableClassName: Package.type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormats: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
      property: 'creative_formats',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormats',
      mutation: UPDATE_PACKAGE_CREATIVEFORMATS,
      editableClassName: Package.type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormatExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormatExclusion', { n: 2 })),
      property: 'creative_format_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormatExclusions',
      mutation: UPDATE_PACKAGE_CREATIVEFORMATEXCLUSIONS,
      editableClassName: Package.type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    unitTags: new EntityProperty({
      label: capitalize(i18n.global.t('names.tag', { n: 2 })),
      property: 'unit_tags',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTags',
      mutation: UPDATE_PACKAGE_UNITTAGS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    unitTagExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.tagExclusion', { n: 2 })),
      property: 'unit_tag_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTagExclusions',
      mutation: UPDATE_PACKAGE_UNITTAGEXCLUSIONS,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    // countries: getLocationEntityProperty('names.country', 0),
    regions: getLocationEntityProperty('names.region', 1),
    cities: getLocationEntityProperty('names.city', 3),
    countryExclusions: getLocationEntityProperty('names.countryExclusion', 0, true),
    regionExclusions: getLocationEntityProperty('names.regionExclusion', 1, true),
    cityExclusions: getLocationEntityProperty('names.cityExclusion', 3, true),
    languages: new EntityProperty({
      label: capitalize(i18n.global.t('names.language', { n: 2 })),
      property: 'language_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'languageIDs',
      mutation: UPDATE_PACKAGE_LANGUAGE_IDS,
      editableClassName: Package.type,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
      action,
      getExtraValidation,
    }),
    languageExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.languageExclusion', { n: 2 })),
      property: 'language_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'languageExclusionIDs',
      mutation: UPDATE_PACKAGE_LANGUAGE_EXCLUSION_IDS,
      editableClassName: Package.type,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
      action,
      getExtraValidation,
    }),
    sites: new EntityProperty({
      label: capitalize(i18n.global.t('names.site', { n: 2 })),
      property: 'site_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteIDs',
      mutation: UPDATE_PACKAGE_SITE_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    siteExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.siteExclusion', { n: 2 })),
      property: 'site_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteExclusionIDs',
      mutation: UPDATE_PACKAGE_SITE_EXCLUSION_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    screens: new EntityProperty({
      label: capitalize(i18n.global.t('names.screen', { n: 2 })),
      property: 'unit_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitIDs',
      mutation: UPDATE_PACKAGE_UNIT_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) =>
        isCuratorOrganization()
          ? rootStore.dispatch('exchApp/getUnits', { queryParams })
          : rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    screenExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenExclusion', { n: 2 })),
      property: 'unit_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitExclusionIDs',
      mutation: UPDATE_PACKAGE_UNITPACK_EXCLUSION_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) =>
        isCuratorOrganization()
          ? rootStore.dispatch('exchApp/getUnits', { queryParams })
          : rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroups: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroup', { n: 2 })),
      property: 'geofencegroup_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupIDs',
      mutation: UPDATE_PACKAGE_GEOFENCE_GROUP_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroupExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroupExclusion', { n: 2 })),
      property: 'geofencegroup_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupExclusionIDs',
      mutation: UPDATE_PACKAGE_GEOFENCE_GROUP_EXCLUSION_IDS,
      editableClassName: Package.type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    targetCriteriaWeather: new EntityProperty({
      label: capitalize(i18n.global.t('names.weatherCondition', { n: 2 })),
      property: 'targetCriteriaWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('names.temperature', { n: 1 })),
      property: 'targetCriteriaTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaUV: new EntityProperty({
      label: capitalize(i18n.global.t('messages.uvTargeting', { n: 1 })),
      property: 'targetCriteriaUV',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaPollen: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pollenTargeting', { n: 1 })),
      property: 'targetCriteriaPollen',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationWeather: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetWeatherConditions')),
      property: 'targetCriteriaDestinationWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetTemperature')),
      property: 'targetCriteriaDestinationTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationRain: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetRainAccumulation')),
      property: 'targetCriteriaDestinationRain',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationSnow: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetSnowAccumulation')),
      property: 'targetCriteriaDestinationSnow',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    customFields: new EntityProperty({
      label: capitalize(i18n.global.t('names.customField', { n: 2 })),
      property: 'customFields',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'customFields',
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    iabCategories: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
      property: 'iab_categories',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyName: 'iabCategories',
      mutation: UPDATE_PACKAGE_IABCATEGORIES,
      editableClassName: Package.type,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
      action,
      getExtraValidation,
    }),
    iabCategoryExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategoryExclusion', { n: 2 })),
      property: 'iab_category_exclusions',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyName: 'iabCategoryExclusions',
      mutation: UPDATE_PACKAGE_IABCATEGORYEXCLUSIONS,
      editableClassName: Package.type,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
      action,
      getExtraValidation,
    }),
    minSecondsBetweenPlaysMin: new EntityProperty({
      label: capitalize(i18n.global.t('names.minSecondsBetweenPlaysMin', { n: 2 })),
      property: 'min_seconds_between_plays_min',
      type: TableColumnTypes.integer,
      editablePropertyName: 'minSecondsBetweenPlaysMin',
    }),
    audienceFile: new EntityProperty({
      label: capitalize(i18n.global.t('names.audienceFile', { n: 1 })),
      property: 'demographic_ids',
      type: TableColumnTypes.integer,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'demographicIDs',
      mutation: UPDATE_PACKAGE_DEMOGRAPHIC_TARGET_IDS,
      editableClassName: Package.type,
      source: () =>
        rootStore.dispatch('orgApp/getDemographics', {
          queryParams: QueryParams.all(FilterCollection.for('type', ['AUDIENCE-FILE'])),
        }),
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
    }),
    demographics: new EntityProperty({
      label: capitalize(i18n.global.t('names.demographic', { n: 2 })),
      property: 'demographic_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'demographicIDs',
      mutation: UPDATE_PACKAGE_DEMOGRAPHIC_TARGET_IDS,
      editableClassName: Package.type,
      source: ({ queryParams = QueryParams.all() }) => {
        return rootStore.dispatch('orgApp/getDemographics', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(FilterCollection.for('demographic_id', entity.demographicIDs)),
    }),
    entityFees: new EntityProperty({
      label: capitalize(i18n.global.t('names.markup')),
      property: 'entity_fees',
      type: TableColumnTypes.list,
      editablePropertyName: 'entityFees',
      editableClassName: Package.type,
      editablePropertyAcceptsMultipleValues: true,
      action,
      getExtraValidation,
    }),
    spendSteeringBudgetMode: new EntityProperty({
      label: `${capitalize(i18n.global.t('names.spendSteering'))} - ${capitalize(i18n.global.t('names.budgetMode'))}`,
      property: 'spendSteeringBudgetMode',
      type: TableColumnTypes.string,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    spendSteeringDimensionType: new EntityProperty({
      label: `${capitalize(i18n.global.t('names.spendSteering'))} - ${capitalize(i18n.global.t('names.dimensionType'))}`,
      property: 'spendSteeringDimensionType',
      type: TableColumnTypes.string,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
    spendSteeringDimensions: new EntityProperty({
      label: `${capitalize(i18n.global.t('names.spendSteering'))} - ${capitalize(i18n.global.t('names.dimension', { n: 2 }))}`,
      property: 'spendSteeringDimensions',
      type: TableColumnTypes.list,
      editableClassName: Package.type,
      action,
      getExtraValidation,
    }),
  };
};
